/**
 * ScheduleList Component
 * Displays a list of schedules with filtering and grouping by active/inactive status.
 */
import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Skeleton,
  Chip,
  Alert,
  Button,
  Grid,
} from '@mui/material';
import { Add as AddIcon, CalendarMonth as CalendarIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSchedules } from '../../hooks/useSchedules';
import { useDevice } from '../../contexts/DeviceContext';
import { ScheduleCard, SendNowProps } from './ScheduleCard';
import { Schedule, ScheduleType } from '../../types/schedule';

type FilterType = 'all' | 'routines' | 'daily' | 'boards';

interface ScheduleListProps {
  onCreateNew: () => void;
  onEdit: (schedule: Schedule) => void;
  onDuplicate: (schedule: Schedule) => void;
  onDelete: (schedule: Schedule) => void;
  onToggleActive: (schedule: Schedule) => void;
  /** Send now for a card, or undefined for none (BA-85) */
  sendNowFor?: (schedule: Schedule) => SendNowProps | undefined;
  /** The schedule the live sentSchedule node says is on the tablet */
  onTabletScheduleId?: string | null;
}

const FILTER_TO_TYPES: Record<FilterType, ScheduleType[] | null> = {
  all: null,
  routines: ['routine'],
  daily: ['daily_schedule'],
  boards: ['first_then', 'choice_board', 'waiting_board'],
};

export const ScheduleList: React.FC<ScheduleListProps> = ({
  onCreateNew,
  onEdit,
  onDuplicate,
  onDelete,
  onToggleActive,
  sendNowFor,
  onTabletScheduleId = null,
}) => {
  const { t } = useTranslation();
  const { selectedDevice } = useDevice();
  const { schedules, loading, error } = useSchedules(selectedDevice?.id ?? '');
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');

  // Filter schedules by type
  const filteredSchedules = useMemo(() => {
    const allowedTypes = FILTER_TO_TYPES[activeFilter];
    if (!allowedTypes) return schedules;
    return schedules.filter((s) => allowedTypes.includes(s.type));
  }, [schedules, activeFilter]);

  // Group schedules by active status
  const { activeSchedules, inactiveSchedules } = useMemo(() => {
    return {
      activeSchedules: filteredSchedules.filter((s) => s.isActive),
      inactiveSchedules: filteredSchedules.filter((s) => !s.isActive),
    };
  }, [filteredSchedules]);

  if (loading) {
    return (
      <Box>
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((i) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={i}>
              <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 1 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {t('schedules.errors.failedToLoadSchedules')}
      </Alert>
    );
  }

  // Empty state
  if (schedules.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6,
          px: 2,
          textAlign: 'center',
        }}
      >
        <CalendarIcon sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          {t('schedules.noSchedulesYet')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
          {t('schedules.noSchedulesDescription')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onCreateNew}
          size="large"
        >
          {t('schedules.createNew')}
        </Button>
      </Box>
    );
  }

  const renderScheduleGrid = (scheduleList: Schedule[], title?: string) => {
    if (scheduleList.length === 0) return null;

    return (
      <Box sx={{ mb: 3 }}>
        {title && (
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ mb: 1.5, fontWeight: 500 }}
          >
            {title}
          </Typography>
        )}
        <Grid container spacing={2}>
          {scheduleList.map((schedule) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={schedule.id}>
              <ScheduleCard
                schedule={schedule}
                onClick={() => onEdit(schedule)}
                onEdit={() => onEdit(schedule)}
                onDuplicate={() => onDuplicate(schedule)}
                onDelete={() => onDelete(schedule)}
                onToggleActive={() => onToggleActive(schedule)}
                sendNow={sendNowFor?.(schedule)}
                isOnTablet={schedule.id === onTabletScheduleId}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  return (
    <Box>
      {/* Filter chips */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {(['all', 'routines', 'daily', 'boards'] as FilterType[]).map((filter) => (
          <Chip
            key={filter}
            label={t(`schedules.filters.${filter}`)}
            onClick={() => setActiveFilter(filter)}
            color={activeFilter === filter ? 'primary' : 'default'}
            variant={activeFilter === filter ? 'filled' : 'outlined'}
            sx={{ cursor: 'pointer' }}
          />
        ))}
      </Box>

      {/* Schedule count */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
        {t('schedules.count', { count: filteredSchedules.length })}
      </Typography>

      {/* No matches for filter */}
      {filteredSchedules.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No schedules match the current filter.
        </Typography>
      )}

      {/* Active schedules */}
      {renderScheduleGrid(
        activeSchedules,
        inactiveSchedules.length > 0 ? t('schedules.filters.active') : undefined
      )}

      {/* Inactive schedules */}
      {renderScheduleGrid(inactiveSchedules, t('schedules.filters.inactive'))}
    </Box>
  );
};
